<template>
  <div
    :data-id="dataId"
    class="relative block flex-shrink-0"
    :class="{ 'opacity-75': disabled }"
  >
    <label v-if="label" :data-id="dataId" class="mb-1 relative">
      <BlTextBody3 :data-id="dataId" :text="label" />
      <span
        v-if="asterisk"
        :data-id="dataId"
        class="text-error-400 text-xl absolute -top-2 ml-1"
      >
        *
      </span>
    </label>
    <div
      :data-id="dataId"
      class="relative"
      :class="disabled ? 'cursor-not-allowed' : ''"
    >
      <div
        :data-id="dataId"
        :class="[
          focus ? 'border-light-purple' : 'border-grey-200',
          dense ? 'h-8 pl-2 pr-6' : 'h-10 pl-4 pr-8',
          disabled ? 'pointer-events-none bg-grey-100' : 'bg-pure-white'
        ]"
        class="relative w-full flex items-center gap-3 border rounded cursor-pointer"
        @click.prevent="focus = !focus"
      >
        <BlTextBody2
          v-if="value.length"
          :data-id="dataId"
          :text="getLabel(value)"
          class="text-slate-1000 truncate"
        />
        <BlTextBody2
          v-else
          :data-id="dataId"
          :text="placeholder"
          class="text-slate-600 truncate"
        />
        <BlIcon
          :data-id="id"
          :icon="'chevron-down'"
          class="absolute w-4 h-4 transition-all duration-300"
          :class="[
            dense ? 'right-1' : 'right-3',
            disabled ? 'pointer-events-none cursor-not-allowed' : ''
          ]"
          :style="{ rotate: focus ? '180deg' : '' }"
        />
      </div>
      <div
        :data-id="id"
        v-if="!disabled && focus"
        class="mt-2 absolute max-h-48 min-w-48 w-full flex-shrink-0 z-50 bg-pure-white rounded border border-grey-200 overflow-y-auto shadow-md"
      >
        <div
          :data-id="id"
          class="h-9 sticky top-0 z-10 w-full bg-slate-300 flex items-center justify-center"
        >
          <BlTextSubtitle3
            v-if="!value.length"
            :data-id="id"
            :text="`Select timeslots`"
            class="text-slate-800"
          />
          <BlTextSubtitle3
            v-else
            :text="`Selected timeslots: ${getLabel(value)}`"
            :data-id="id"
            class="text-slate-800"
          />
        </div>
        <div
          v-if="!options.length"
          :data-id="id"
          class="flex justify-center gap-2 p-3"
        >
          <BlTextBody2 :data-id="id" :text="`No timeslot available`" />
        </div>
        <div v-else :data-id="id" class="flex flex-wrap gap-2 p-3">
          <div
            v-for="opt in options"
            :key="opt.value"
            :data-id="id"
            class="h-7.5 w-28 flex items-center justify-center rounded flex-shrink-0 transition-all duration-300 border border-slate-400"
            :class="[
              value.includes(opt.value) ? 'bg-light-purple' : 'bg-pure-white',
              opt.disabled ? 'cursor-default bg-slate-200' : 'cursor-pointer'
            ]"
            @click.prevent="onToggle(opt)"
            :title="opt.disabled ? 'Booked' : getLabel([opt.value])"
          >
            <BlTextBody2
              :data-id="id"
              :class="[
                value.includes(opt.value)
                  ? 'text-pure-white'
                  : 'text-slate-1000'
              ]"
              class="flex-shrink-0 transition-all duration-300"
              :text="getLabel([opt.value])"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CLOCK_HALF } from "@/plugins/assets.js";

import BlIcon from "./BlIcon.vue";
import BlTextSubtitle3 from "./BlTextSubtitle3.vue";
import BlTextBody2 from "./BlTextBody2.vue";
import BlTextBody3 from "./BlTextBody3.vue";

export default {
  name: "BlSelectTimeslot",
  components: { BlIcon, BlTextSubtitle3, BlTextBody3, BlTextBody2 },
  props: {
    dataId: { type: String, default: "" },
    label: { type: String },
    placeholder: { type: String, default: "Select" },
    disabled: { type: Boolean },
    asterisk: { type: Boolean },
    value: { default: () => [] }, // string[]
    options: { default: () => [] }, // { value, disabled }[]
    dense: { type: Boolean, default: false }
    // @change(value)
  },
  data() {
    return {
      focus: false,
      clockHalf: CLOCK_HALF
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      if (this.dataId) {
        return `${this.dataId}-bl-select-timeslot-${combination}`;
      }
      return `bl-select-timeslot-${combination}`;
    }
  },
  watch: {
    focus(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  methods: {
    getLabel(arr) {
      let idx = 0;
      if (arr.length === 1) {
        idx = this.clockHalf.indexOf(arr[0]);
      } else {
        idx = this.clockHalf.indexOf(arr[arr.length - 1]);
      }
      return `${arr[0]} - ${this.clockHalf[idx + 1]}`;
    },
    onToggle({ value, disabled }) {
      if (disabled) return;

      let arr = [...this.value];
      const remove = arr.includes(value);
      if (remove) {
        arr = arr.filter(i => i !== value);
      } else {
        arr.push(value);
      }
      arr.sort((a, b) => this.clockHalf.indexOf(a) - this.clockHalf.indexOf(b));
      if (arr.length > 1) {
        const firstIndex = this.clockHalf.indexOf(arr[0]);
        const lastIdx = this.clockHalf.indexOf(arr[arr.length - 1]);
        const valueIdx = this.clockHalf.indexOf(value);
        const currentIndex = lastIdx < valueIdx ? lastIdx : valueIdx;
        arr = this.clockHalf.filter(
          (_, idx) => idx >= firstIndex && idx <= currentIndex
        );
      }
      let hasDisabled = false;
      arr.forEach(i => {
        const opt = this.options.find(o => o.value === i);
        if (!opt) hasDisabled = true;
        else if (opt.disabled) hasDisabled = true;
      });
      if (hasDisabled) {
        arr = [value];
      }
      this.$emit("change", arr);
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.focus = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/BlSelectTimeslot.vue